/**
 * Soundtrack mixer for the story player.
 *
 * Graph:  deck A ─┐
 *                 ├─ duck gain ─ master gain ─ speakers
 *         deck B ─┘
 *
 * Two decks allow true crossfades between tracks (and a seamless loop when
 * there is only one). Gains are ramped with Web Audio, which is sample-
 * accurate and click-free, and works on iOS where `audio.volume` is
 * read-only.
 *
 * Browsers only allow audio after a user gesture, so the AudioContext is
 * created lazily and `unlock()` must be called from a click/tap/keypress.
 * Nothing here plays unless real tracks were supplied: Memoryflix never
 * invents music.
 */

export interface MixerTrack {
  id: string;
  url: string;
  title: string;
}

/** Music level while a video with its own sound plays (fraction of normal). */
export const DUCK_LEVEL = 0.18;
/** Music level under the voice-over: clearly there, never over the words. */
export const VOICE_DUCK_LEVEL = 0.22;
const FADE_IN_S = 2.5;
const CROSSFADE_S = 4;
const DUCK_RAMP_S = 0.4;
const DUCK_RELEASE_S = 1.2;

interface Deck {
  el: HTMLAudioElement;
  gain: GainNode;
  trackIndex: number;
}

export type MixerState = 'idle' | 'locked' | 'playing' | 'paused' | 'stopped' | 'error';

export class SoundtrackMixer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private duck: GainNode | null = null;
  private decks: Deck[] = [];
  private active = 0;
  private tracks: MixerTrack[] = [];
  private volume = 0.6;
  /** 1 = normal; lower while something else speaks (video sound, narration). */
  private duckLevel = 1;
  private crossfading = false;
  private pauseTimer: number | null = null;
  private listeners = new Set<() => void>();
  state: MixerState = 'idle';
  currentTitle: string | null = null;
  lastError: string | null = null;

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
  private emit() {
    for (const fn of this.listeners) fn();
  }

  get hasTracks() {
    return this.tracks.length > 0;
  }

  /** True when audio may start without another gesture. */
  get unlocked() {
    return this.ctx?.state === 'running';
  }

  /**
   * Must run inside a user-gesture handler (click, tap, key) to succeed.
   * Without a gesture some browsers leave `resume()` pending forever, so it
   * is raced against a short timeout instead of awaited blindly.
   */
  async unlock(): Promise<boolean> {
    try {
      if (!this.ctx) this.build();
      const ctx = this.ctx!;
      if (ctx.state !== 'running') {
        await Promise.race([ctx.resume(), new Promise((r) => window.setTimeout(r, 300))]);
      }
      const ok = ctx.state === 'running';
      if (ok && this.state === 'locked') {
        this.state = 'idle';
        this.emit();
      }
      return ok;
    } catch {
      return false;
    }
  }

  private build() {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const master = ctx.createGain();
    const duck = ctx.createGain();
    master.gain.value = 0;
    duck.gain.value = this.duckLevel;
    duck.connect(master).connect(ctx.destination);
    this.decks = [0, 1].map(() => {
      const el = new Audio();
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      const gain = ctx.createGain();
      gain.gain.value = 0;
      ctx.createMediaElementSource(el).connect(gain).connect(duck);
      const deck: Deck = { el, gain, trackIndex: -1 };
      el.addEventListener('timeupdate', () => this.maybeCrossfade(deck));
      el.addEventListener('error', () => this.onDeckError(deck));
      return deck;
    });
    this.ctx = ctx;
    this.master = master;
    this.duck = duck;
    // Resuming can finish after unlock() has stopped waiting; whenever the
    // context does become audible, release a mixer that was waiting for it.
    ctx.addEventListener('statechange', () => {
      if (ctx.state === 'running' && this.state === 'locked') this.state = 'idle';
      this.emit();
    });
  }

  setTracks(tracks: MixerTrack[]) {
    const changed = tracks.map((t) => t.id).join() !== this.tracks.map((t) => t.id).join();
    this.tracks = tracks;
    if (changed && (this.state === 'playing' || this.state === 'paused')) {
      // Playlist edited mid-film: fade to the new first track.
      if (tracks.length === 0) this.stop(0.8);
      else this.startDeck(this.active === 0 ? 1 : 0, 0, CROSSFADE_S / 2);
    }
    this.emit();
  }

  private ramp(param: AudioParam, to: number, seconds: number) {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(to, now + Math.max(0.01, seconds));
  }

  /** Begin the soundtrack from the first track with a gentle fade-in. */
  async start() {
    if (this.tracks.length === 0) return;
    if (!this.unlocked) {
      // Waiting for a gesture; the player shows a "Tap for sound" prompt.
      if (this.state !== 'locked') {
        this.state = 'locked';
        this.emit();
      }
      return;
    }
    this.clearPauseTimer();
    this.ramp(this.master!.gain, this.volume, FADE_IN_S);
    await this.startDeck(this.active, 0, 0.05);
  }

  private async startDeck(deckIndex: number, trackIndex: number, fadeS: number) {
    const incoming = this.decks[deckIndex];
    const outgoing = this.decks[deckIndex === 0 ? 1 : 0];
    const track = this.tracks[trackIndex % this.tracks.length];
    if (!track) return;
    incoming.trackIndex = trackIndex % this.tracks.length;
    if (incoming.el.src !== new URL(track.url, location.href).href) incoming.el.src = track.url;
    incoming.el.currentTime = 0;
    try {
      await incoming.el.play();
    } catch (err) {
      this.state = (err as Error).name === 'NotAllowedError' ? 'locked' : 'error';
      this.lastError = (err as Error).message;
      this.emit();
      return;
    }
    this.ramp(incoming.gain.gain, 1, fadeS);
    if (deckIndex !== this.active || outgoing.el.src) {
      this.ramp(outgoing.gain.gain, 0, fadeS);
      const old = outgoing.el;
      window.setTimeout(() => {
        if (this.decks[this.active] !== outgoing) old.pause();
      }, fadeS * 1000 + 100);
    }
    this.active = deckIndex;
    this.crossfading = false;
    this.currentTitle = track.title;
    this.state = 'playing';
    this.emit();
  }

  /** Near the end of a track, crossfade into the next (or loop the only one). */
  private maybeCrossfade(deck: Deck) {
    if (deck !== this.decks[this.active] || this.crossfading || this.state !== 'playing') return;
    const { duration, currentTime } = deck.el;
    if (!Number.isFinite(duration) || duration < CROSSFADE_S * 2) return;
    if (duration - currentTime > CROSSFADE_S) return;
    this.crossfading = true;
    void this.startDeck(this.active === 0 ? 1 : 0, deck.trackIndex + 1, CROSSFADE_S);
  }

  private onDeckError(deck: Deck) {
    if (deck !== this.decks[this.active]) return;
    this.lastError = 'A soundtrack file could not be played';
    // Skip to the next track rather than going silent for the whole film.
    if (this.tracks.length > 1) void this.startDeck(this.active === 0 ? 1 : 0, deck.trackIndex + 1, 1);
    else {
      this.state = 'error';
      this.emit();
    }
  }

  private clearPauseTimer() {
    if (this.pauseTimer !== null) window.clearTimeout(this.pauseTimer);
    this.pauseTimer = null;
  }

  /** Fade down quickly, then actually pause the element (saves battery). */
  pause() {
    if (this.state !== 'playing' || !this.ctx) return;
    this.ramp(this.master!.gain, 0, 0.4);
    this.clearPauseTimer();
    this.pauseTimer = window.setTimeout(() => {
      for (const d of this.decks) d.el.pause();
    }, 450);
    this.state = 'paused';
    this.emit();
  }

  async resume() {
    if (this.state !== 'paused' || !this.ctx) return;
    this.clearPauseTimer();
    const deck = this.decks[this.active];
    try {
      await deck.el.play();
    } catch {
      this.state = 'locked';
      this.emit();
      return;
    }
    this.ramp(this.master!.gain, this.volume, 0.6);
    this.state = 'playing';
    this.emit();
  }

  /** Fade out and stop (end of the film, or music switched off). */
  stop(fadeS = 2.5) {
    if (!this.ctx || (this.state !== 'playing' && this.state !== 'paused')) {
      this.state = this.tracks.length ? 'idle' : this.state;
      return;
    }
    this.ramp(this.master!.gain, 0, fadeS);
    this.clearPauseTimer();
    this.pauseTimer = window.setTimeout(() => {
      for (const d of this.decks) {
        d.el.pause();
        d.gain.gain.value = 0;
      }
    }, fadeS * 1000 + 100);
    this.state = 'stopped';
    this.emit();
  }

  setVolume(v: number) {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.ctx && this.state === 'playing') this.ramp(this.master!.gain, this.volume, 0.15);
  }

  /**
   * Lower the music under a video's own sound or the voice-over (a fraction
   * of normal; 1 restores it). Going down is quick so the first word is
   * clear; coming back up is slower so it doesn't jump.
   */
  setDuckLevel(level: number) {
    const next = Math.min(1, Math.max(0, level));
    if (this.duckLevel === next) return;
    const rising = next > this.duckLevel;
    this.duckLevel = next;
    if (this.ctx) this.ramp(this.duck!.gain, next, rising ? DUCK_RELEASE_S : DUCK_RAMP_S);
    this.emit();
  }

  get isDucked() {
    return this.duckLevel < 1;
  }

  /** Live values for diagnostics and tests. */
  snapshot() {
    const deck = this.decks[this.active];
    return {
      state: this.state,
      error: this.lastError,
      context: this.ctx?.state ?? 'none',
      master: this.master?.gain.value ?? 0,
      duck: this.duck?.gain.value ?? 1,
      volume: this.volume,
      ducked: this.duckLevel < 1,
      duckLevel: this.duckLevel,
      track: this.currentTitle,
      position: deck?.el.currentTime ?? 0,
      elementPaused: deck?.el.paused ?? true,
      decks: this.decks.map((d, i) => ({
        active: i === this.active,
        gain: Number(d.gain.gain.value.toFixed(2)),
        paused: d.el.paused,
        time: Number(d.el.currentTime.toFixed(1)),
      })),
    };
  }

  destroy() {
    this.clearPauseTimer();
    for (const d of this.decks) {
      d.el.pause();
      d.el.removeAttribute('src');
      d.el.load();
    }
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.listeners.clear();
  }
}
